import { paymentApi } from '@/features/booking/api/payment.api';
import type { InitiatePaymentResponse, Payment } from '@/types';
import { launchPaymentCheckout } from './paymentCheckout';

const VERIFY_ATTEMPTS = 8;
const VERIFY_DELAY_MS = 2000;

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function verifyPaymentWithRetry(paymentId: string): Promise<Payment> {
  const idempotencyKey = `verify_${paymentId}`;
  let lastPayment: Payment | null = null;

  for (let attempt = 0; attempt < VERIFY_ATTEMPTS; attempt++) {
    lastPayment = await paymentApi.verify(paymentId, idempotencyKey, { keepSession: true });
    if (lastPayment.paymentStatus === 'SUCCESS') {
      return lastPayment;
    }
    if (lastPayment.paymentStatus === 'FAILED') {
      throw new Error('Payment was not completed. Please try again.');
    }
    if (attempt < VERIFY_ATTEMPTS - 1) {
      await sleep(VERIFY_DELAY_MS);
    }
  }

  throw new Error(
    lastPayment?.paymentStatus === 'PENDING'
      ? 'Payment is still processing. Check My Bookings in a moment.'
      : 'Payment was not completed. Please try again.'
  );
}

export async function verifyPaymentByOrderWithRetry(providerOrderId: string): Promise<Payment> {
  const idempotencyKey = `verify_order_${providerOrderId}`;
  let lastPayment: Payment | null = null;

  for (let attempt = 0; attempt < VERIFY_ATTEMPTS; attempt++) {
    lastPayment = await paymentApi.verifyByOrderId(providerOrderId, idempotencyKey, {
      keepSession: true,
    });
    if (lastPayment.paymentStatus === 'SUCCESS') {
      return lastPayment;
    }
    if (lastPayment.paymentStatus === 'FAILED') {
      throw new Error('Payment was not completed. Please try again.');
    }
    if (attempt < VERIFY_ATTEMPTS - 1) {
      await sleep(VERIFY_DELAY_MS);
    }
  }

  throw new Error(
    lastPayment?.paymentStatus === 'PENDING'
      ? 'Payment is still processing. Check My Bookings in a moment.'
      : 'Payment was not completed. Please try again.'
  );
}

/**
 * Opens Cashfree checkout. For modal sessions, verifies payment with the server
 * after the gateway closes so booking status updates from PAYMENT_PENDING.
 */
export async function completePaymentCheckout(
  payment: InitiatePaymentResponse
): Promise<Payment | null> {
  if (payment.alreadyPaid || payment.payment.paymentStatus === 'SUCCESS') {
    return payment.payment;
  }

  if (payment.checkoutUrl) {
    await launchPaymentCheckout(payment);
    return null;
  }

  await launchPaymentCheckout(payment);
  const paymentId = paymentApi.getStoredPaymentId() ?? payment.payment.id;
  return verifyPaymentWithRetry(paymentId);
}
